/** RoyalEngagement — Family Section */

import React from 'react';
import { motion } from 'framer-motion';
import { Users } from 'lucide-react';
import { ROYAL_EASE } from '../theme';
import type { RoyalContent, FamilyGroup } from '../content';

function FamilyColumn({ group, delay }: { group: FamilyGroup; delay: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.7, delay, ease: ROYAL_EASE }}
      className="flex flex-col items-center"
    >
      <div className="flex flex-col items-center gap-3 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--royal-maroon)]/12 text-[var(--royal-gold)]">
          <Users size={22} />
        </span>
        <h3 className="font-serif text-2xl font-semibold text-[var(--royal-ink)]">
          {group.title}
        </h3>
        <span className="text-[0.66rem] font-semibold uppercase tracking-[0.24em] text-[var(--royal-maroon)]">
          {group.side === 'groom' ? "Groom's Side" : "Bride's Side"}
        </span>
      </div>

      <span className="my-5 h-8 w-px bg-[var(--royal-border)]" />

      <div className="flex w-full flex-col gap-3">
        {group.members.map((member, i) => (
          <motion.div
            key={`${member.name}-${i}`}
            initial={{ opacity: 0, x: group.side === 'groom' ? -24 : 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5, delay: i * 0.08, ease: ROYAL_EASE }}
            className="rounded-2xl border border-[var(--royal-border)] bg-[var(--royal-ivory-dark)] px-5 py-4 text-center shadow-sm relative overflow-hidden"
          >
            {/* Gold accent line */}
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[var(--royal-gold)] to-transparent" />
            <p className="font-medium text-[var(--royal-ink)]">{member.name}</p>
            <p className="mt-0.5 text-sm text-[var(--royal-muted)]">
              {member.relation}
            </p>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}

export function FamiliesSection({
  content,
}: {
  content: RoyalContent['families'];
}) {
  return (
    <section id="families" className="relative w-full px-5 py-20 sm:px-8 sm:py-24 lg:px-12 lg:py-32 bg-[var(--royal-ivory)] text-[var(--royal-ink)]">
      <div className="mx-auto w-full max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 36 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, ease: ROYAL_EASE }}
          className="flex flex-col gap-4 items-center text-center"
        >
          <span className="inline-flex items-center gap-2 text-[0.7rem] font-semibold uppercase tracking-[0.32em] text-[var(--royal-maroon)]">
            <span className="h-px w-6 bg-[var(--royal-gold)]" />
            {content.label}
          </span>
          <h2 className="font-serif text-[clamp(2rem,5vw,3.4rem)] font-semibold leading-[1.08] text-[var(--royal-ink)]">
            {content.title}
          </h2>
        </motion.div>

        <div className="relative mt-14 grid gap-12 md:grid-cols-2 md:gap-8">
          {content.groups.map((group, i) => (
            <FamilyColumn key={group.title} group={group} delay={i * 0.12} />
          ))}

          {/* Joining ornament */}
          <div className="pointer-events-none absolute left-1/2 top-9 hidden -translate-x-1/2 md:block">
            <motion.span
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, ease: ROYAL_EASE }}
              className="block h-4 w-4 rounded-full bg-gradient-to-br from-[var(--royal-gold)] to-[var(--royal-maroon)] ring-4 ring-[var(--royal-gold)]/20"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
